import React, { useEffect, useState } from 'react';
import api from '../services/api';

export type WorkerLocationOption = {
  marketKey: string;
  marketLabel: string;
  locality: string;
  region: string;
  value: string;
};

export const findWorkerLocationOption = (
  options: WorkerLocationOption[],
  value: string | null | undefined,
) => {
  const normalized = String(value || '').trim().toLowerCase();
  return options.find(option => option.value.toLowerCase() === normalized) || null;
};

export const useWorkerLocationOptions = (enabled = true) => {
  const [options, setOptions] = useState<WorkerLocationOption[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError('');
    api.get('/users/location-options')
      .then(response => {
        if (!active) return;
        const next = Array.isArray(response.data?.options) ? response.data.options : [];
        setOptions(next);
        if (next.length === 0) setError('Supported locations are unavailable right now. Please try again.');
      })
      .catch(() => {
        if (active) setError('Supported locations are unavailable right now. Please try again.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [enabled]);

  return { options, loading, error };
};

type Props = {
  id: string;
  label: string;
  options: WorkerLocationOption[];
  selected: WorkerLocationOption | null;
  onChange: (option: WorkerLocationOption | null) => void;
  currentLocation?: string;
  loading?: boolean;
  error?: string;
  required?: boolean;
  preserveCurrent?: boolean;
};

const WorkerLocationSelect = ({
  id,
  label,
  options,
  selected,
  onChange,
  currentLocation,
  loading = false,
  error = '',
  required = false,
  preserveCurrent = false,
}: Props) => {
  const groups = options.reduce<Record<string, WorkerLocationOption[]>>((result, option) => {
    (result[option.marketLabel] ||= []).push(option);
    return result;
  }, {});
  const descriptionId = `${id}-description`;
  const errorId = `${id}-error`;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        id={id}
        value={selected?.value || ''}
        onChange={(event) => onChange(findWorkerLocationOption(options, event.target.value))}
        disabled={loading || Boolean(error)}
        required={required}
        aria-describedby={`${descriptionId}${error ? ` ${errorId}` : ''}`}
        aria-invalid={Boolean(error)}
        className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
      >
        <option value="">
          {loading
            ? 'Loading supported locations…'
            : preserveCurrent && currentLocation
              ? `Keep current: ${currentLocation}`
              : 'Select a supported city and state…'}
        </option>
        {Object.entries(groups).map(([marketLabel, marketOptions]) => (
          <optgroup key={marketLabel} label={marketLabel}>
            {marketOptions.map(option => (
              <option key={`${option.locality}|${option.region}`} value={option.value}>
                {option.value}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <p id={descriptionId} className="mt-1 text-xs text-gray-500">
        {currentLocation && !selected
          ? `Current saved location: ${currentLocation}. Choose a supported location only when you want to correct it.`
          : 'StageCrew uses this explicit city and state for local job matching. It does not infer or geocode your location.'}
      </p>
      {error && <p id={errorId} role="alert" className="mt-1 text-sm text-red-700">{error}</p>}
    </div>
  );
};

export default WorkerLocationSelect;
